import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/services/api'

export interface TeamProduct {
  product_slug: string
}

export interface TeamMember {
  id: number
  user_email: string
  role: 'admin' | 'manager' | 'standard' | 'readonly'
  added_by: string
  created_at: string
}

export interface Team {
  id: number
  name: string
  description: string
  created_at: string
  updated_at: string
  members: TeamMember[]
  products: TeamProduct[]
}

export interface AccessLogEntry {
  id: number
  user_email: string
  action: string
  resource_type: string
  resource_id: string
  details: Record<string, unknown>
  timestamp: string
}

export function useTeams() {
  return useQuery({
    queryKey: ['teams'],
    queryFn: async () => {
      const res = await api.get<Team[]>('/auth/teams/')
      return res.data
    },
  })
}

export function useCreateTeam() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: { name: string; description?: string }) => {
      const res = await api.post<Team>('/auth/teams/', data)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['teams'] }),
  })
}

export function useUpdateTeam() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...data }: { id: number; name?: string; description?: string }) => {
      const res = await api.patch<Team>(`/auth/teams/${id}/`, data)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['teams'] }),
  })
}

export function useDeleteTeam() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/auth/teams/${id}/`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['teams'] }),
  })
}

export function useAddTeamMember() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: { teamId: number; user_email: string; role: string }) => {
      const res = await api.post<TeamMember>(`/auth/teams/${data.teamId}/members/`, {
        user_email: data.user_email,
        role: data.role,
      })
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['teams'] }),
  })
}

export function useRemoveTeamMember() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: { teamId: number; memberId: number }) => {
      await api.delete(`/auth/teams/${data.teamId}/members/${data.memberId}/`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['teams'] }),
  })
}

export function useSetTeamProducts() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: { teamId: number; product_slugs: string[] }) => {
      const res = await api.put(`/auth/teams/${data.teamId}/products/`, {
        product_slugs: data.product_slugs,
      })
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['teams'] }),
  })
}

export function useAccessLogs() {
  return useQuery({
    queryKey: ['rbac-access-logs'],
    queryFn: async () => {
      const res = await api.get<AccessLogEntry[]>('/auth/access-logs/')
      return res.data
    },
  })
}
